import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { inject, Injectable } from '@angular/core';
import { ParametersService } from '../../parameters/services/parameters.service';
import { AnalyseBreadcrumbAction, GetJsonFile, InitAnalyse, SendFilter, SendParameters } from './analyse.actions';
import { tap, catchError } from 'rxjs';
import { BreadcrumbItem } from 'src/app/shared/types/breadcrumb.type';
import { Parameters } from '../../parameters/type/parameters.type';
import { ParametersStatus } from '../../parameters/type/parameters_status.enum';
import { ParametersPayloadResponseType } from '../../parameters/type/parameters_payload_response.type';

export type AnalyseStateModel = {
  isLoading: boolean;
  step: number;
  parameters: Parameters[] | null;
  groupedDataReady?: boolean;
};

const expectedKeys: (keyof ParametersPayloadResponseType)[] = [
  'article_desc', 'type', 'sender', 'Type_vehicule', 'Transport_type',
  'Date_transport', 'Destination', 'Origine', 'Transporteur', 'article_fam'
];

function transformDictionaryToArray(dict: { [key: string]: string[] }): ParametersPayloadResponseType[] {
  const maxLength = Math.max(...Object.values(dict).map(arr => arr.length));
  const result: ParametersPayloadResponseType[] = [];

  for (let i = 0; i < maxLength; i++) {
    const obj: any = {};
    expectedKeys.forEach(key => {
      obj[key] = dict[key]?.[i] ?? '';
    });
    // Vérification stricte
    if (expectedKeys.every(key => key in obj)) {
      result.push(obj as ParametersPayloadResponseType);
    } else {
      throw new Error(`Clé(s) manquante(s) à l’index ${i}: ${JSON.stringify(obj)}`);
    }
  }
  return result;
}

@State<AnalyseStateModel>({
  name: 'parameters',
  defaults: {
    step: 1,
    isLoading: false,
    parameters: null
  }
})
@Injectable()
export class AnalyseState {
  private readonly parametersService = inject(ParametersService);
  private readonly store = inject(Store);

  @Selector()
  static getParameters(state: AnalyseStateModel): Parameters[] | null {
    return state.parameters;
  }

  @Selector()
  static isLoading(state: AnalyseStateModel): boolean {
    return state.isLoading;
  }

  @Selector()
  static showAnalyse(state: AnalyseStateModel): boolean {
    if (!state.parameters || state.parameters.length === 0) {
      return false;
    }
    return state.parameters.every(param => param.status === ParametersStatus.TREATED);
  }

  @Selector()
  static getStep(state: AnalyseStateModel): number {
    return state.step;
  }

  @Selector()
  static getBreadcrumbs(state: AnalyseStateModel): BreadcrumbItem[] {
    const step = state.step;

    return [
      {
        step: 1,
        label: 'breadcrumb.scenario.perimeters',
        isActive: step >= 1,
        isValidate: step > 1,
        action: step > 1 ? new AnalyseBreadcrumbAction(1) : undefined
      },
      {
        step: 2,
        label: 'breadcrumb.analyse.parameters',
        isActive: step >= 2,
        isValidate: step > 2,
        action: step > 2 ? new AnalyseBreadcrumbAction(2) : undefined
      },
      {
        step: 3,
        label: 'breadcrumb.analyse.synthesis',
        isActive: step >= 3,
        isValidate: step > 3,
        action: step > 3 ? new AnalyseBreadcrumbAction(3) : undefined
      },
      {
        step: 4,
        label: 'breadcrumb.analyse.view',
        isActive: step >= 4,
        isValidate: step > 4
      },
    ];
  }

  @Selector()
  static canDecarbonize(state: AnalyseStateModel): boolean {
    return true;
  }

  @Selector()
  static isGroupedDataReady(state: AnalyseStateModel): boolean {
    return !!state.groupedDataReady;
  }

  @Action(InitAnalyse)
  initAnalyse({ patchState, getState }: StateContext<AnalyseStateModel>) {
    patchState({ isLoading: true });
    const savedStep = localStorage.getItem('analyseStep');
    const step = savedStep ? parseInt(savedStep, 10) : 1;
    return this.parametersService.getParameters().pipe(
      tap((parameters) => {
        patchState({
          isLoading: false,
          step,
          parameters: Array.isArray(parameters) ? parameters : [parameters],
        });
      }),
      catchError((error) => {
        patchState({ isLoading: false });
        throw error;
      })
    );
  }

  @Action(AnalyseBreadcrumbAction)
  goToStep({ patchState }: StateContext<AnalyseStateModel>, { step }: AnalyseBreadcrumbAction): void {
    patchState({ step });
    localStorage.setItem('analyseStep', step.toString());
  }

  @Action(SendParameters)
  sendParameters(
    { patchState }: StateContext<AnalyseStateModel>,
    { parameters }: SendParameters
  ) {
    const paramsArray = Array.isArray(parameters) ? parameters : [parameters];
    patchState({ isLoading: true });

    return this.parametersService.sendParameters(paramsArray).pipe(
      tap((newParameters) => {
        patchState({
          isLoading: false,
          step: 3,
          parameters: Array.isArray(newParameters) ? newParameters : [newParameters],
        });
        localStorage.setItem('analyseStep', '3');
      }),
      catchError((error) => {
        patchState({
          isLoading: false,
          parameters: null
        });
        throw error;
      })
    );
  }

  @Action(GetJsonFile)
  getJsonFile(
    { patchState }: StateContext<AnalyseStateModel>,
    { field }: GetJsonFile
  ) {
    return this.parametersService.getJsonFile(field).pipe(
      tap((jsonDict: { [key: string]: string[] }) => {
        try {
          const array = transformDictionaryToArray(jsonDict);

          const grouped: { [key: string]: string[] } = {};
          array.forEach(item => {
            Object.keys(item).forEach(key => {
              const typedKey = key as keyof ParametersPayloadResponseType;
              if (!grouped[typedKey]) {
                grouped[typedKey] = [];
              }
              const value = item[typedKey];
              grouped[typedKey].push(Array.isArray(value) ? value.join(', ') : (value ?? ''));
            });
          });

          Object.keys(grouped).forEach(key => {
            const filteredValues = grouped[key].filter(value => value && value.trim() !== '');
            localStorage.setItem(`groupedDataJson.${key}`, JSON.stringify(filteredValues));
          });
          patchState({ groupedDataReady: true });
        } catch (error) {
          patchState({ groupedDataReady: false });
        }
      })
    );
  }

  @Action(SendFilter)
  sendFilter(
    { getState }: StateContext<AnalyseStateModel>,
    action: SendFilter
  ) {
    return this.parametersService.sendFilter(action.field);
  }

}
